import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import { createStaff } from "../../actions/staffActions";
import { clearStaffCreated, clearStaffError } from "../../slices/staffSlice";
import { getdesignations } from "../../actions/designationActions";
import { toInputDate } from "../../utils/formatDate";
import AppDatePicker from "../common/AppDatePicker";

export default function NewStaff() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, isStaffCreated, error } = useSelector((state) => state.staffState);
    const { designations = [] } = useSelector((state) => state.designationState);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [designation, setDesignation] = useState("");
    const [qualification, setQualification] = useState("BNYS");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [parentPhone, setParentPhone] = useState("");
    const [monthlySalary, setMonthlySalary] = useState("");
    const [joiningDate, setJoiningDate] = useState(toInputDate());
    const [remarks, setRemarks] = useState("");
    const [idProof, setIdProof] = useState([]);
    const [resume, setResume] = useState(null);

    useEffect(() => {
        dispatch(clearStaffCreated());
        dispatch(clearStaffError());
        dispatch(getdesignations());
        return () => {
            dispatch(clearStaffCreated());
            dispatch(clearStaffError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isStaffCreated) {
            toast("Staff added successfully!", { type: "success" });
            dispatch(clearStaffCreated());
            navigate("/admin/staff");
        }
        if (error) {
            toast(error, { type: "error" });
            dispatch(clearStaffError());
        }
    }, [isStaffCreated, error, dispatch, navigate]);

    const submitHandler = (e) => {
        e.preventDefault();

        if (idProof.length === 0) {
            toast("Please upload at least one ID proof", { type: "warning" });
            return;
        }

        const formData = new FormData();
        formData.append("name", name);
        formData.append("email", email);
        formData.append("designation", designation);
        formData.append("qualification", qualification);
        formData.append("phone", phone);
        formData.append("address", address);
        formData.append("parentPhone", parentPhone);
        formData.append("monthlySalary", monthlySalary || 0);
        formData.append("joiningDate", joiningDate);
        formData.append("remarks", remarks);
        idProof.forEach((file) => formData.append("idProof", file));
        if (resume) formData.append("resume", resume);

        dispatch(createStaff(formData));
    };

    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
                <Fragment>
                    <div className="container my-4">
                        <form onSubmit={submitHandler} className="card shadow p-4" encType="multipart/form-data">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h3 className="mb-0">Add Staff</h3>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/admin/staff")}>
                                    &larr; Back to Staff
                                </button>
                            </div>

                            <div className="form-group mb-3">
                                <label>Name</label>
                                <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
                            </div>

                            <div className="form-group mb-3">
                                <label>Email</label>
                                <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} required />
                            </div>

                            <div className="form-group mb-3">
                                <label>Designation</label>
                                <select 
                                    className="form-control" 
                                    value={designation} 
                                    onChange={(e) => setDesignation(e.target.value)} 
                                    required
                                >
                                    <option value="">-- Select Designation --</option>
                                    {designations.map(d => (
                                        <option key={d._id} value={d.name}>{d.name}</option>
                                    ))}
                                </select>
                            </div>

                            {designation && designation.toLowerCase().includes('doctor') && (
                                <div className="form-group mb-3 bg-light p-3 rounded border">
                                    <label className="fw-semibold text-success"><i className="fa fa-user-md me-1"></i> Doctor Qualification / Degree</label>
                                    <input className="form-control" placeholder="e.g. BNYS or BNYS, MD" value={qualification} onChange={(e) => setQualification(e.target.value)} />
                                    <small className="text-muted">This medical degree (e.g. BNYS) will automatically appear on official prescriptions and diet charts.</small>
                                </div>
                            )}

                            <div className="form-group mb-3">
                                <label>Phone</label>
                                <input className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} required />
                            </div>

                            <div className="form-group mb-3">
                                <label>Address</label>
                                <textarea className="form-control" value={address} onChange={(e) => setAddress(e.target.value)} required />
                            </div>

                            <div className="form-group mb-3">
                                <label>Parent / Guardian phone number</label>
                                <input className="form-control" value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} />
                            </div>

                            <div className="form-group mb-3">
                                <label>Monthly Salary (for attendance-based calculation)</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    value={monthlySalary}
                                    onChange={(e) => setMonthlySalary(e.target.value)}
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label>Joining Date</label>
                                <AppDatePicker
                                    label="Joining Date"
                                    value={joiningDate}
                                    onChange={(val) => setJoiningDate(val)}
                                    required
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label>ID Proof (one or more)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    multiple
                                    accept="image/*,application/pdf"
                                    onChange={(e) => setIdProof(Array.from(e.target.files))}
                                />
                                {idProof.length > 0 && (
                                    <small className="text-muted">{idProof.length} file(s) selected</small>
                                )}
                            </div>

                            <div className="form-group mb-3">
                                <label>Resume (optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept="image/*,application/pdf"
                                    onChange={(e) => setResume(e.target.files[0] || null)}
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label>Remarks (optional)</label>
                                <textarea className="form-control" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                            </div>

                            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                                {loading ? "Saving..." : "Add Staff"}
                            </button>
                        </form>
                    </div>
                </Fragment>
        </div>
        </div>
    );
}



